import { Suspense, lazy, useState, useEffect, useRef } from 'react'
import Overlays from './Overlays'
import EndCard from './EndCard'
import Backgrounds from './Backgrounds'
import { scrollState } from './scrollState'

// Lazy-load the entire 3D layer (Canvas + Scene + R3F + three.js + drei + GLB
// loader + Particles) so the initial bundle is text+images only. The 3D chunk
// loads in parallel while the user reads Beats 1–3; by the time they reach
// Beat 4 (~t=0.43) it's ready. Importing Canvas itself out of App.jsx is what
// actually moves three.js out of the main bundle.
const Canvas3D = lazy(() => import('./Canvas3D'))

export default function App() {
  const [ready, setReady] = useState(false)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [isMobile, setIsMobile] = useState(false)
  // Conditional Canvas mount: Canvas3D is null until the user scrolls toward
  // Beat 4. Before that, no continuous 3D rendering = no main-thread blocking.
  // We pre-warm the lazy chunk earlier (at t > 0.10) so by the time we mount,
  // the JS is in cache and mount is instant.
  const [show3D, setShow3D] = useState(false)
  const show3DRef = useRef(false)
  const prefetchedRef = useRef(false)

  useEffect(() => {
    const update = () => {
      setSize({ w: window.innerWidth, h: window.innerHeight })
      setIsMobile(window.matchMedia('(max-width: 768px)').matches)
    }
    update()
    window.addEventListener('resize', update)
    setReady(true)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const t = scrollState.offset
      // Warm the lazy chunk cache as the user moves past Beat 1.
      if (!prefetchedRef.current && t > 0.10) {
        prefetchedRef.current = true
        import('./Canvas3D')
      }
      // Mount the Canvas just before Beat 4's promise window starts.
      if (!show3DRef.current && t > 0.35) {
        show3DRef.current = true
        setShow3D(true)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Mobile auto-starts like desktop — the branded #aqi-splash loader in
  // index.html covers the load phase; no tap gate, no reduced-motion fork.
  // (The old StaticFallback drew #222 text on the #0a0a0a body = invisible
  // black screen for every iOS user with Reduce Motion on. All motion here
  // is scroll-driven by the user, so the full experience is served instead.)
  if (!ready) return null

  return (
    <>
      <Backgrounds isMobile={isMobile} />

      <div style={{ position: 'fixed', inset: 0, zIndex: 2, width: size.w, height: size.h, pointerEvents: 'none' }}>
        {show3D && (
          <Suspense fallback={null}>
            <Canvas3D size={size} isMobile={isMobile} />
          </Suspense>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 3 }}>
        <Overlays isMobile={isMobile} />
      </div>

      <EndCard />

      <BottomBar />
    </>
  )
}

// Bottom bar — tri-link CTA in the Scroll-hint typography. Reuses the same
// font, letter-spacing and colour as the original scroll hint so it reads as
// part of the cinematic vocabulary rather than UI chrome. Scroll-hint fades
// out early (t > 0.55) once the user is past Beat 1; KNOW MORE + TALK TO US
// persist until the EndCard centres in (t > 0.92).
function BottomBar() {
  const [, force] = useState(0)
  useEffect(() => {
    let raf
    const loop = () => {
      force((n) => (n + 1) & 0xff)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [])
  const t = scrollState.offset
  const scrollVisible = Math.max(0, 1 - Math.max(0, (t - 0.55)) * 4)
  const linksVisible = Math.max(0, 1 - Math.max(0, (t - 0.90)) * 25)
  if (linksVisible <= 0.01) return null

  const linkStyle = {
    pointerEvents: 'auto',
    fontSize: '0.74rem',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.7)',
    textDecoration: 'none',
    padding: '0.5rem 0.25rem',
    whiteSpace: 'nowrap',
  }
  const scrollHintStyle = {
    fontSize: '0.74rem',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.55)',
    animation: 'pulse 2.5s ease-in-out infinite',
    pointerEvents: 'none',
    opacity: scrollVisible,
    whiteSpace: 'nowrap',
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0 1rem',
        pointerEvents: 'none',
        zIndex: 5,
        opacity: linksVisible,
        transition: 'opacity 0.15s linear',
      }}
    >
      <a
        href="https://aqi0.com/air-purifier/"
        target="_blank"
        rel="noopener noreferrer"
        style={linkStyle}
      >
        Know more →
      </a>
      {scrollVisible > 0.01 ? (
        <div style={scrollHintStyle}>Scroll ↓</div>
      ) : null}
      <a
        href="https://wa.me/919667672740?text=Hi%20aqi0%2C%20I%20saw%20the%20%2Fair%2F%20page%20-%20can%20we%20talk%3F"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Talk to us on WhatsApp"
        style={linkStyle}
      >
        Talk to us →
      </a>
    </div>
  )
}

